import { 
  ShoppingBagIcon,
  ClockIcon,
  TagIcon,
  ChevronRightIcon,
  XIcon,
  CheckCircleIcon
} from '../Icons'
import { Product } from '../../data/products'

interface LabOrder {
  id: string
  items: Product[]
  total: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'paid'
  createdAt: string
  customerName: string
}

interface OrdersScreenProps {
  onLog: (message: string) => void
  orders: Product[]
  labOrders?: LabOrder[]
  onRemoveOrder?: (index: number) => void
  onProceedToPayment?: () => void
  onCreateOrder?: () => void
  onPayOrder?: (orderId: string) => void
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  blue: '#3b82f6',
  red: '#ef4444',
}

export default function OrdersScreen({ 
  onLog, 
  orders, 
  labOrders,
  onRemoveOrder, 
  onProceedToPayment,
  onCreateOrder,
  onPayOrder 
}: OrdersScreenProps) {
  const totalAmount = orders.reduce((sum, product) => {
    const priceStr = (product.price || '0').replace(/[^\d]/g, '')
    return sum + (parseInt(priceStr) || 0)
  }, 0)

  const handleViewDetails = (product: Product) => {
    onLog(`Просмотр деталей: ${product.title}`)
  }

  const handleRemove = (index: number, product: Product) => {
    if (onRemoveOrder) {
      onRemoveOrder(index)
      onLog(`❌ Удалён из корзины: ${product.title}`)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return colors.green
      case 'paid': return colors.blue
      case 'cancelled': return colors.red
      default: return colors.yellow
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '📦 Ожидает оплаты'
      case 'confirmed': return '✅ Подтверждён'
      case 'paid': return '💳 Оплачен'
      case 'cancelled': return '❌ Отменён'
      default: return status
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      paddingBottom: '100px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Заголовок */}
      <div style={{
        marginBottom: '24px',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '28px',
          fontWeight: 700,
          marginBottom: '8px',
        }}>
          {labOrders ? 'Мои заказы' : 'Корзина'}
        </div>
        <div style={{
          color: colors.textMuted,
          fontSize: '14px',
        }}>
          {labOrders 
            ? `${labOrders.length} ${labOrders.length === 1 ? 'заказ' : labOrders.length < 5 ? 'заказа' : 'заказов'}`
            : `${orders.length} ${orders.length === 1 ? 'товар' : orders.length < 5 ? 'товара' : 'товаров'} в корзине`
          }
        </div>
      </div>

      {/* Статистика */}
      {orders.length > 0 && (
        <div style={{
          background: `linear-gradient(135deg, ${colors.accent}20, transparent)`,
          border: `1px solid ${colors.accentBorder}`,
          borderRadius: '14px',
          padding: '20px',
          marginBottom: '24px',
        }}>
          <div style={{
            color: colors.textMuted,
            fontSize: '12px',
            marginBottom: '8px',
          }}>
            Общая сумма заказов
          </div>
          <div style={{
            color: colors.accentLight,
            fontSize: '32px',
            fontWeight: 700,
          }}>
            {totalAmount.toLocaleString('ru-RU')} ₽
          </div>
        </div>
      )}

      {/* Список заказов */}
      {orders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
        }}>
          <div style={{
            marginBottom: '16px',
            opacity: 0.3,
          }}>
            <ShoppingBagIcon size={64} color={colors.textMuted} />
          </div>
          <div style={{
            color: colors.text,
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '8px',
          }}>
            Нет заказов
          </div>
          <div style={{
            color: colors.textMuted,
            fontSize: '14px',
          }}>
            Выберите продукты на главной странице
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {orders.map((product, index) => (
            <div
              key={`${product.id}-${index}`}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: '12px',
                padding: '16px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '12px',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '10px',
                  background: `${colors.accent}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShoppingBagIcon size={24} color={colors.accentLight} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    color: colors.text,
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '4px',
                  }}>
                    {product.title}
                  </div>
                  <div style={{
                    color: colors.textMuted,
                    fontSize: '12px',
                    marginBottom: '8px',
                  }}>
                    {product.description}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    {product.duration && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: colors.textMuted,
                      }}>
                        <ClockIcon size={12} />
                        {product.duration}
                      </div>
                    )}
                    {product.level && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: colors.textMuted,
                      }}>
                        <TagIcon size={12} />
                        {product.level}
                      </div>
                    )}
                  </div>
                </div>

                {/* Кнопка удаления - красный крестик */}
                {onRemoveOrder && (
                  <button
                    onClick={() => handleRemove(index, product)}
                    style={{
                      background: `${colors.red}15`,
                      border: `1px solid ${colors.red}30`,
                      borderRadius: '8px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    <XIcon size={16} color={colors.red} />
                  </button>
                )}
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: `1px solid ${colors.cardBorder}`,
              }}>
                <div style={{
                  color: colors.accentLight,
                  fontSize: '18px',
                  fontWeight: 700,
                }}>
                  {product.price}
                </div>

                <button
                  onClick={() => handleViewDetails(product)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.cardBorder}`,
                    borderRadius: '8px',
                    padding: '8px 14px',
                    color: colors.text,
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Подробнее
                  <ChevronRightIcon size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Кнопка оплаты - показываем если есть заказы */}
      {orders.length > 0 && onProceedToPayment && (
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={onProceedToPayment}
            style={{
              width: '100%',
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              border: 'none',
              borderRadius: '12px',
              padding: '18px',
              color: colors.text,
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: `0 4px 16px ${colors.accent}40`,
            }}
          >
            💳 Перейти к оплате • ₽{totalAmount.toLocaleString()}
          </button>
        </div>
      )}

      {/* Кнопка создать заказ - для лаборатории */}
      {orders.length > 0 && onCreateOrder && (
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={onCreateOrder}
            style={{
              width: '100%',
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              border: 'none',
              borderRadius: '12px',
              padding: '18px',
              color: colors.text,
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: `0 4px 16px ${colors.accent}40`,
            }}
          >
            📦 Создать заказ • ₽{totalAmount.toLocaleString()}
          </button>
        </div>
      )}

      {/* Список заказов (LabOrders) */}
      {labOrders && labOrders.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{
            color: colors.text,
            fontSize: '20px',
            fontWeight: 700,
            marginBottom: '16px',
          }}>
            История заказов
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {labOrders.map((order) => (
              <div
                key={order.id}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.cardBorder}`,
                  borderRadius: '12px',
                  padding: '16px',
                  opacity: order.status === 'cancelled' ? 0.5 : 1,
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div>
                    <div style={{
                      color: colors.text,
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '4px',
                    }}>
                      Заказ {order.id}
                    </div>
                    <div style={{
                      color: colors.textMuted,
                      fontSize: '12px',
                    }}>
                      {order.items.length} {order.items.length === 1 ? 'товар' : 'товара'}
                    </div>
                  </div>
                  <div style={{
                    color: colors.accentLight,
                    fontSize: '18px',
                    fontWeight: 700,
                  }}>
                    ₽{order.total.toLocaleString()}
                  </div>
                </div>

                {/* Статус */}
                <div style={{
                  background: `${getStatusColor(order.status)}15`,
                  border: `1px solid ${getStatusColor(order.status)}40`,
                  borderRadius: '8px',
                  padding: '10px 12px',
                  marginBottom: '12px',
                  color: getStatusColor(order.status),
                  fontSize: '13px',
                  fontWeight: 600,
                  textAlign: 'center',
                }}>
                  {getStatusLabel(order.status)}
                </div>

                {/* Кнопка оплаты - для pending заказов */}
                {order.status === 'pending' && onPayOrder && (
                  <button
                    onClick={() => onPayOrder(order.id)}
                    style={{
                      width: '100%',
                      background: `linear-gradient(135deg, ${colors.green}, #10b981)`,
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon size={18} />
                    Оплатить заказ
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
